import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Collapse,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Upload,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { ImportSessionStatusResponse } from '@store/generated/admin';
import { useListOrganizationImportSessionsQuery } from '../store/hooks';
import {
  getProgressPhaseDescription,
  getShortStatusLabel,
  getStatusColor,
  isSessionProcessing,
  getPollingInterval,
} from '../utils/import-status-helpers';

interface DataMigrationSessionsListProps {
  organizationId: string;
  onRefresh?: boolean;
}

interface SessionRowProps {
  session: ImportSessionStatusResponse;
  organizationId: string;
}

// Helper function to get status icon
const getStatusIcon = (status: string) => {
  const statusLower = status?.toLowerCase();

  switch (statusLower) {
    case 'pending':
      return <ScheduleIcon fontSize="small" />;
    case 'extracting':
    case 'matching':
    case 'importing':
      return <CircularProgress size={16} />;
    case 'ready_for_review':
      return <VisibilityIcon fontSize="small" />;
    case 'completed':
      return <CheckCircleIcon fontSize="small" />;
    case 'failed':
      return <ErrorIcon fontSize="small" />;
    case 'cancelled':
      return <DeleteIcon fontSize="small" />;
    default:
      return undefined;
  }
};

function SessionRow({ session, organizationId }: SessionRowProps) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    // Format the full date and time
    const fullDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    // Calculate relative time
    let relativeTime: string;
    if (diffInHours < 1) {
      relativeTime = 'Just now';
    } else if (diffInHours < 24) {
      relativeTime = `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays === 1) {
        relativeTime = 'Yesterday';
      } else if (diffInDays < 7) {
        relativeTime = `${diffInDays} days ago`;
      } else {
        relativeTime = '';
      }
    }

    return { fullDate, time, relativeTime };
  };

  const hasErrors = session.error_messages && session.error_messages.length > 0;
  const isReadyForReview = session.status?.toLowerCase() === 'ready_for_review';
  const isCompleted = session.status?.toLowerCase() === 'completed';
  const isProcessing = isSessionProcessing(session.status);

  const handleViewDetails = () => {
    navigate(`/admin-v2/organization/${organizationId}/data-migrations/session/${session.session_id}`);
  };

  const dateInfo = formatDate(session.created_at);

  return (
    <>
      <TableRow
        hover
        onClick={handleViewDetails}
        sx={{ cursor: 'pointer' }}>
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {dateInfo.fullDate} at {dateInfo.time}
            </Typography>
            {dateInfo.relativeTime && (
              <Typography variant="caption" color="text.secondary">
                {dateInfo.relativeTime}
              </Typography>
            )}
          </Box>
        </TableCell>

        <TableCell>
          <Chip
            label={getShortStatusLabel(session.status)}
            color={getStatusColor(session.status)}
            size="small"
            icon={getStatusIcon(session.status)}
            variant="outlined"
          />
        </TableCell>

        <TableCell>
          {isProcessing ? (
            <Box sx={{ width: '100%', maxWidth: 150 }}>
              <LinearProgress
                variant="determinate"
                value={session.progress_percentage || 0}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Box display="flex" justifyContent="space-between" mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {session.progress_percentage || 0}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(() => {
                    // Use "patients" label for reasonable numbers to clarify vs percentage confusion
                    const total = session.total_patients || 0;
                    const processed = session.processed_patients || 0;
                    return total <= 1000 ? `${processed}/${total} patients` : `${processed}/${total}`;
                  })()}
                </Typography>
              </Box>
              <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                {(() => {
                  const total = session.total_patients || 0;
                  const processed = session.processed_patients || 0;
                  return total <= 1000
                    ? getProgressPhaseDescription(session.status, session.progress_percentage, processed, total)
                    : getProgressPhaseDescription(session.status, session.progress_percentage);
                })()}
              </Typography>
            </Box>
          ) : isReadyForReview ? (
            <Typography variant="body2" color="info.main">
              Ready for Review
            </Typography>
          ) : isCompleted ? (
            <Typography variant="body2" color="success.main">
              Import Completed
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          )}
        </TableCell>

        <TableCell>
          <Box>
            <Typography variant="body2">
              {(() => {
                // Use "patients" for reasonable numbers to clarify vs percentage confusion
                const total = session.total_patients || 0;
                const label = total <= 1000 ? 'patients' : 'records';
                return (
                  <>
                    <strong>{total}</strong> {label}
                    {session.new_patients ? ` • ${session.new_patients} new` : ''}
                    {session.failed_patients ? ` • ${session.failed_patients} failed` : ''}
                  </>
                );
              })()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isReadyForReview ? 'Ready for admin review and import decision' :
               isCompleted ? 'Import process completed successfully' :
               isProcessing ? (() => {
                 const processed = session.processed_patients || 0;
                 const total = session.total_patients || 0;
                 return total <= 1000
                   ? `Processing (${processed} of ${total} patients done)`
                   : `Processing (${processed} done)`;
               })() :
               session.status?.toLowerCase() === 'failed' ? 'Processing failed - check errors' :
               session.status?.toLowerCase() === 'cancelled' ? 'Import was cancelled' :
               'Status pending'}
            </Typography>
          </Box>
        </TableCell>

        <TableCell>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" fontWeight="medium">
              {session.file_type?.toUpperCase() || 'Unknown'}
            </Typography>
            {hasErrors && (
              <>
                <Tooltip title="Session has errors">
                  <ErrorIcon color="warning" fontSize="small" />
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(!expanded);
                  }}
                >
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>

      {hasErrors && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Error Messages
                </Typography>
                {session.error_messages?.map((error: any, index: number) => (
                  <Alert
                    key={index}
                    severity="error"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2">
                      {typeof error === 'string' ? (
                        error
                      ) : (
                        <>
                          <strong>{error.error_type || 'Error'}:</strong> {error.message}
                        </>
                      )}
                    </Typography>
                    {typeof error !== 'string' && error.timestamp && (
                      <Typography variant="caption" color="text.secondary">
                        {(() => {
                          const dateInfo = formatDate(error.timestamp);
                          return `${dateInfo.fullDate} at ${dateInfo.time}`;
                        })()}
                      </Typography>
                    )}
                  </Alert>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function DataMigrationSessionsList({ organizationId, onRefresh }: DataMigrationSessionsListProps) {
  const { data, isLoading, error, refetch } = useListOrganizationImportSessionsQuery({
    organizationId,
  });

  // Dynamic polling based on session statuses
  useEffect(() => {
    if (!data?.sessions || data.sessions.length === 0) {
      return;
    }

    // Find the most urgent polling interval needed
    let shortestInterval: number | false = false;

    data.sessions.forEach((session: ImportSessionStatusResponse) => {
      const interval = getPollingInterval(session.status);
      if (interval !== false) {
        if (shortestInterval === false || interval < shortestInterval) {
          shortestInterval = interval;
        }
      }
    });

    if (shortestInterval) {
      const intervalId = setInterval(() => {
        refetch();
      }, shortestInterval);

      return () => clearInterval(intervalId);
    }
  }, [data, refetch]);

  // Refresh when onRefresh prop changes
  useEffect(() => {
    if (onRefresh) {
      refetch();
    }
  }, [onRefresh, refetch]);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading sessions...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Failed to load migration sessions. Please try again.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!data?.sessions || data.sessions.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box textAlign="center" py={8}>
            <Upload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Data Review Sessions Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload patient data files to review them before deciding whether to import, merge, or discard.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Upload for Review" to start reviewing patient data.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            Data Review Sessions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.total} session{data.total !== 1 ? 's' : ''} to review
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date/Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Records</TableCell>
                <TableCell>File Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.sessions.map((session: ImportSessionStatusResponse) => (
                <SessionRow
                  key={session.session_id}
                  session={session}
                  organizationId={organizationId}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}